import {
  useEffect,
  useMemo,
  useRef,
} from 'react';
import Select, {
  components,
  GroupBase,
  IndicatorsContainerProps,
  InputProps,
} from 'react-select';

import { MultiOptionValue } from '@/core/interfaces/common';
import { FieldHelp } from '@/core/components/FieldHelp';

import {
  fieldDescribedBy,
  fieldDomId,
  fieldErrorId,
} from '@/utils/fieldIds';

interface FormMultipleSelectProps {
  backgroundcolor: string;
  error: () => string | false;
  /**
   * Optional description of why the field exists or how it is used.
   * Always exposed to screen readers via aria-describedby; visually
   * revealed on demand through a small help toggle inside the field.
   */
  helpText?: string;
  isDisabled?: boolean;
  isRequired?: boolean;
  onMultiChange: (values: Array<MultiOptionValue>) => void;
  onFocus: () => void;
  /**
   * Optional callback invoked when the browser fires the `invalid` event on the underlying input.
   * Called after the native tooltip is suppressed via `event.preventDefault()`.
   * Use this to set a styled inline error message in place of the browser's native tooltip.
   */
  onInvalidField?: () => void;
  name: string;
  options: Array<string>;
  values: Array<MultiOptionValue> | undefined;
  ariaLabel?: string;
}

export const FormMultipleSelect = ({
  backgroundcolor,
  error,
  helpText,
  isDisabled,
  isRequired,
  onMultiChange,
  onFocus,
  onInvalidField,
  options,
  values = [],
  name,
  ariaLabel,
}: FormMultipleSelectProps) => {
  const containerRef = useRef<HTMLDivElement>(null);

  const errorMessage = error();

  const describedBy = fieldDescribedBy({
    fieldName: name,
    hasError: !!errorMessage,
    hasHelp: !!helpText,
  });

  // react-select sets aria-describedby on its inner input itself
  // (pointing at its placeholder or live region) and ignores the
  // aria-describedby prop, so the help and error ids have to be
  // appended through a custom Input component.
  const Input = useMemo(() => {
    const WithDescription = (
      props: InputProps<MultiOptionValue, true, GroupBase<MultiOptionValue>>
    ) => {
      const { 'aria-describedby': reactSelectDescribedBy } = props;

      return (
        <components.Input
          {...props}
          aria-describedby={
            [
              reactSelectDescribedBy,
              describedBy,
            ].filter(Boolean).join(' ') || undefined
          }
        />
      );
    };

    return WithDescription;
  }, [describedBy]);

  // Render the help toggle inline with react-select's own indicators so
  // it never overlaps selected values and the control never grows wider.
  // Mouse and touch events are stopped so using the toggle does not open
  // the menu.
  const IndicatorsContainer = useMemo(() => {
    const WithFieldHelp = (
      props: IndicatorsContainerProps<MultiOptionValue, true, GroupBase<MultiOptionValue>>
    ) => {
      const { children } = props;

      return (
        <components.IndicatorsContainer {...props}>
          {helpText && (
            /* eslint-disable-next-line jsx-a11y/no-static-element-interactions */
            <span
              onClick={event => event.stopPropagation()}
              onKeyDown={event => event.stopPropagation()}
              onMouseDown={event => {
                event.preventDefault();
                event.stopPropagation();
              }}
              onTouchEnd={event => event.stopPropagation()}
            >
              <FieldHelp
                fieldName={name}
                text={helpText}
              />
            </span>
          )}
          {children}
        </components.IndicatorsContainer>
      );
    };

    return WithFieldHelp;
  }, [
    helpText,
    name,
  ]);

  useEffect(() => {
    const container = containerRef.current;

    if (!container || !onInvalidField) {
      return undefined;
    }

    const handleInvalid = (event: Event) => {
      event.preventDefault();
      onInvalidField();
    };

    container.addEventListener('invalid', handleInvalid, true);

    return () => container.removeEventListener('invalid', handleInvalid, true);
  }, [onInvalidField]);

  const selectOptions = options.map(option => ({
    label: option,
    value: option,
  }));

  const placeholderText = isRequired ? `${name} *` : name;

  return (
    <div
      ref={containerRef}
      style={{
        paddingTop: '21px',
        position: 'relative',
      }}
    >
      {values.length > 0 && (
        <span
          style={{
            fontSize: '12px',
            left: 0,
            lineHeight: '1',
            position: 'absolute',
            top: 4,
          }}
        >
          {name}
          {isRequired && <span style={{ color: '#8B0000' }}> *</span>}
        </span>
      )}
      <Select
        aria-invalid={!!errorMessage}
        aria-label={ariaLabel || name}
        components={{
          IndicatorsContainer,
          Input,
        }}
        inputId={`${fieldDomId(name)}-input`}
        isDisabled={isDisabled}
        isMulti
        name={name}
        placeholder={placeholderText}
        options={selectOptions}
        styles={{
          control: (baseStyles, state) => ({
            ...baseStyles,
            '&:hover': { borderColor: backgroundcolor },
            borderColor: state.isFocused ? backgroundcolor : 'var(--muster-field-border)',
            ...(error() ? { borderColor: '#d53c2d' } : {}),
            boxShadow: 'none',
            fontSize: '16px',
            marginTop: '0',
            padding: '10px 6px',
          }),
          dropdownIndicator: baseStyles => ({
            ...baseStyles,
            '&:hover': { color: '#757575' },
            color: '#757575',
          }),
          indicatorSeparator: (baseStyles, state) => ({
            ...baseStyles,
            ...(state.hasValue ? {} : { display: 'none' }),
          }),
          option: (baseStyles, state) => {
            let optionBackground = 'white';

            if (state.isSelected) {
              optionBackground = backgroundcolor;
            } else if (state.isFocused) {
              optionBackground = '#F0F0F0';
            }

            return {
              ...baseStyles,
              backgroundColor: optionBackground,
              color: state.isSelected ? 'white' : 'inherit',
              outline: state.isFocused && !state.isSelected ? '2px solid #666666' : 'none',
              outlineOffset: '-2px',
            };
          },
          placeholder: baseStyles => ({
            ...baseStyles,
            color: '#595959',
          }),
        }}
        defaultValue={values}
        onChange={newOptions => onMultiChange([...newOptions])}
        onFocus={onFocus}
        className="custom-field-multi-select"
        classNamePrefix="select"
        required={isRequired}
      />
      <span
        id={fieldErrorId(name)}
        aria-live="assertive"
        aria-atomic="true"
        style={{
          bottom: '-16px',
          color: '#d53c2d',
          fontSize: '12px',
          fontWeight: 700,
          left: 0,
          lineHeight: '14px',
          position: 'absolute',
        }}
      >
        {errorMessage || ''}
      </span>
    </div>
  );
};

FormMultipleSelect.defaultProps = {
  helpText: '',
  isDisabled: false,
  isRequired: false,
};
